import { useEffect, useState } from "react";
import API from "../../services/api";

interface User {
  _id: string;
  full_name: string;
  email: string;
  phone_prefix: string;
  phone_number: string;
  address: string;
  has_active_membership: boolean;
  wallet_balance: number;
  createdAt: string;
  updatedAt: string;
}

interface UserDetailsModalProps {
  user: User;
  onClose: () => void;
}

interface Membership {
  _id: string;
  plan: string;
  status: string;
  start_date: string;
  end_date: string;
  price: number;
  coupon_applied?: string;
  is_free_membership?: boolean;
}

interface Transaction {
  _id: string;
  amount: number;
  type: 'credit' | 'debit';
  purpose: string;
  description: string;
  createdAt: string;
}

export default function UserDetailsModal({ user, onClose }: UserDetailsModalProps) {
  const [membership, setMembership] = useState<Membership | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'membership' | 'transactions'>('info');

  useEffect(() => {
    fetchUserDetails();
  }, [user._id]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const membershipRes = await API.get(`/user/${user._id}/membership`);
      const transactionsRes = await API.get(`/user/${user._id}/transactions?limit=10`);

      if (membershipRes.data.success) {
        setMembership(membershipRes.data.data.active_membership);
      }

      if (transactionsRes.data.success) {
        setTransactions(transactionsRes.data.data.transactions);
      }
    } catch (error: any) {
      setError(error?.response?.data?.message || "Failed to fetch user details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.headerInfo}>
            <div style={styles.avatarPlaceholder}>
              {user.full_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 style={styles.modalTitle}>{user.full_name}</h2>
              <div style={styles.userMeta}>
                <span>{user.email || 'No email'}</span>
                <span style={styles.dot}>•</span>
                <span>{user.phone_prefix} {user.phone_number}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.tabs}>
          <button
            onClick={() => setActiveTab('info')}
            style={{
              ...styles.tab,
              ...(activeTab === 'info' ? styles.tabActive : {})
            }}
          >
            Info
          </button>
          <button
            onClick={() => setActiveTab('membership')}
            style={{
              ...styles.tab,
              ...(activeTab === 'membership' ? styles.tabActive : {})
            }}
          >
            Membership
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            style={{
              ...styles.tab,
              ...(activeTab === 'transactions' ? styles.tabActive : {})
            }}
          >
            Transactions
          </button>
        </div>

        {loading ? (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Loading details...</p>
          </div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : (
          <>
            {activeTab === 'info' && (
              <div style={styles.infoTab}>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <label>Full Name</label>
                    <span>{user.full_name}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <label>Email</label>
                    <span>{user.email || 'Not provided'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <label>Phone</label>
                    <span>{user.phone_prefix} {user.phone_number}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <label>Address</label>
                    <span>{user.address}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <label>Wallet Balance</label>
                    <span style={styles.walletBalance}>₹{user.wallet_balance.toFixed(2)}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <label>Membership</label>
                    <span style={styles.membershipBadge}>
                      {user.has_active_membership ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={styles.infoItem}>
                    <label>Joined</label>
                    <span>{formatDate(user.createdAt)}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'membership' && (
              <div style={styles.membershipTab}>
                {membership ? (
                  <div style={styles.membershipCard}>
                    <div style={styles.membershipHeader}>
                      <span style={styles.membershipPlan}>
                        {membership.plan.replace('_', ' ').toUpperCase()}
                      </span>
                      <span style={styles.membershipStatus}>
                        {membership.status}
                      </span>
                    </div>
                    {membership.is_free_membership && (
                      <div style={styles.freeBadge}>🎁 Free Membership</div>
                    )}
                    <div style={styles.membershipDetails}>
                      <div style={styles.detailRow}>
                        <span>Start Date</span>
                        <span>{formatDate(membership.start_date)}</span>
                      </div>
                      <div style={styles.detailRow}>
                        <span>End Date</span>
                        <span>{formatDate(membership.end_date)}</span>
                      </div>
                      <div style={styles.detailRow}>
                        <span>Price</span>
                        <span>₹{membership.price.toFixed(2)}</span>
                      </div>
                      {membership.coupon_applied && (
                        <div style={styles.detailRow}>
                          <span>Coupon Applied</span>
                          <span style={styles.couponCode}>{membership.coupon_applied}</span>
                        </div>
                      )}
                      {membership.is_free_membership && (
                        <div style={styles.detailRow}>
                          <span>Discount</span>
                          <span style={styles.discountAmount}>₹{membership.price.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={styles.emptyState}>
                    <p>No active membership</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transactions' && (
              <div style={styles.transactionsTab}>
                {transactions.length > 0 ? (
                  <div style={styles.transactionsList}>
                    {transactions.map((transaction) => (
                      <div key={transaction._id} style={styles.transactionItem}>
                        <div style={styles.transactionLeft}>
                          <div style={styles.transactionType}>
                            {transaction.type === 'credit' ? '📈' : '📉'}
                          </div>
                          <div>
                            <div style={styles.transactionPurpose}>
                              {transaction.purpose.replace(/_/g, ' ').toUpperCase()}
                            </div>
                            <div style={styles.transactionDescription}>
                              {transaction.description}
                            </div>
                            <div style={styles.transactionDate}>
                              {formatDate(transaction.createdAt)}
                            </div>
                          </div>
                        </div>
                        <div style={{
                          ...styles.transactionAmount,
                          color: transaction.type === 'credit' ? '#4caf50' : '#f44336'
                        }}>
                          {transaction.type === 'credit' ? '+' : '-'}₹{transaction.amount.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={styles.emptyState}>
                    <p>No transactions found</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "700px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
  },
  headerInfo: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  avatarPlaceholder: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    fontWeight: "600",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1a2332",
    margin: 0,
  },
  userMeta: {
    fontSize: "13px",
    color: "#666",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  dot: {
    color: "#ccc",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    padding: "0 4px",
  },
  tabs: {
    display: "flex",
    gap: "8px",
    borderBottom: "2px solid #e8ecf1",
    marginBottom: "20px",
  },
  tab: {
    padding: "10px 20px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#666",
    transition: "all 0.2s",
    position: "relative" as const,
  },
  tabActive: {
    color: "#6c5ce7",
    fontWeight: "600",
    borderBottom: "2px solid #6c5ce7",
    marginBottom: "-2px",
  },
  loading: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    padding: "40px 20px",
    color: "#666",
  },
  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #6c5ce7",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "12px",
  },
  error: {
    padding: "16px",
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: "6px",
    color: "#d32f2f",
    textAlign: "center" as const,
  },
  infoTab: {
    padding: "8px 0",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  infoItem: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  walletBalance: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#4caf50",
  },
  membershipBadge: {
    fontSize: "14px",
    fontWeight: "500",
  },
  membershipTab: {
    padding: "8px 0",
  },
  membershipCard: {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    padding: "20px",
  },
  membershipHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  membershipPlan: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1a2332",
  },
  membershipStatus: {
    display: "inline-block",
    padding: "4px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    backgroundColor: "#4caf50",
    color: "#fff",
    textTransform: "capitalize" as const,
  },
  freeBadge: {
    display: "inline-block",
    padding: "4px 12px",
    backgroundColor: "#fff3e0",
    color: "#e65100",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "12px",
  },
  membershipDetails: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px solid #e8ecf1",
    fontSize: "14px",
  },
  couponCode: {
    fontFamily: "monospace",
    fontWeight: "600",
    color: "#6c5ce7",
  },
  discountAmount: {
    color: "#4caf50",
    fontWeight: "600",
  },
  transactionsTab: {
    padding: "8px 0",
  },
  transactionsList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  },
  transactionItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
  transactionLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  transactionType: {
    fontSize: "20px",
  },
  transactionPurpose: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#1a2332",
  },
  transactionDescription: {
    fontSize: "12px",
    color: "#888",
  },
  transactionDate: {
    fontSize: "11px",
    color: "#aaa",
    marginTop: "2px",
  },
  transactionAmount: {
    fontSize: "16px",
    fontWeight: "600",
  },
  emptyState: {
    textAlign: "center" as const,
    padding: "40px 20px",
    color: "#888",
  },
};